import React from 'react';
import { css } from '@emotion/core';
import { Field } from '@atlaskit/form';
import TextField from '@atlaskit/textfield';
import TextArea from '@atlaskit/textarea';
import { IndicatorDTOHack, SloInstanceDto } from '@atlassiansox/phobos-client-ts';

import { lessThanUnitStyles } from '@/styles';
import { MAX_TEXT_LENGTH } from '@/constants';
import { FormFieldTooltipLabel } from '@/components/primitive';
import { ConditionalErrorMessage } from '@/components/composed/forms/form-errors';

import { helperCopy } from '../../copy';

import GlanceExperienceFields from './ui-metrics-sli-picker';
import SignalFlowQueryFields, { CheckPayload } from './signalflow-sli-picker';
import { validateSLOTarget } from './utils';

const sloTargetStyles = [
  lessThanUnitStyles('%'),
  css`
    min-width: 125px;
  `,
];

export type CloudWatchSliFormFieldsProps = {
  serviceId: string;
  namespace: string;
  environment: string;
  rollupType: string;
  resourceName: string;
  successExpression: string;
  failureExpression: string;
  indicator?: IndicatorDTOHack;
};

export type SliFormFieldsProps = CheckPayload & {
  isDisabled: boolean;
  type: FieldOption<API.SLOTypeName>;
  uiMetrics?: API.UIMetrics.Config;
  sloInstance?: SloInstanceDto;
} & CloudWatchSliFormFieldsProps;

const SliFormFields: React.FC<SliFormFieldsProps> = ({
  goodQuery,
  badQuery,
  objective,
  type,
  uiMetrics,
  serviceId,
  namespace,
  environment,
  rollupType,
  resourceName,
  successExpression,
  failureExpression,
  isDisabled,
  indicator,
  sloInstance,
}) => (
  <>
    {type.value === 'UI_PERFORMANCE' && (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <GlanceExperienceFields {...uiMetrics} />
    )}
    {(type.value === 'PERFORMANCE' || type.value === 'RELIABILITY') && (
      <SignalFlowQueryFields
        goodQuery={goodQuery}
        badQuery={badQuery}
        isDisabled={isDisabled}
        indicator={indicator}
        sloInstance={sloInstance}
      />
    )}
    {(type.value === 'PERFORMANCE_CLOUDWATCH' || type.value === 'RELIABILITY_CLOUDWATCH') && (
      <>
        <Field
          name="serviceId"
          label="Service ID"
          defaultValue={serviceId ?? ''}
          isDisabled={isDisabled}
        >
          {({ fieldProps, error }) => (
            <>
              <TextField
                isMonospaced
                spellCheck={false}
                maxLength={MAX_TEXT_LENGTH}
                placeholder="micros-deployed-service"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...fieldProps}
              />
              <ConditionalErrorMessage error={error} />
            </>
          )}
        </Field>
        <Field name="namespace" label="Namespace" defaultValue={namespace} isDisabled={isDisabled}>
          {({ fieldProps, error }) => (
            <>
              <TextField
                isMonospaced
                spellCheck={false}
                maxLength={MAX_TEXT_LENGTH}
                placeholder="AWS/ApplicationELB"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...fieldProps}
              />
              <ConditionalErrorMessage error={error} />
            </>
          )}
        </Field>
        <Field
          name="environment"
          label="Environment"
          defaultValue={environment}
          isDisabled={isDisabled}
        >
          {({ fieldProps, error }) => (
            <>
              <TextField
                isMonospaced
                spellCheck={false}
                maxLength={MAX_TEXT_LENGTH}
                placeholder="prod-east"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...fieldProps}
              />
              <ConditionalErrorMessage error={error} />
            </>
          )}
        </Field>
        <Field
          name="rollupType"
          label="Rollup Type"
          defaultValue={rollupType}
          isDisabled={isDisabled}
        >
          {({ fieldProps, error }) => (
            <>
              <TextField
                isMonospaced
                spellCheck={false}
                maxLength={MAX_TEXT_LENGTH}
                placeholder="Sum"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...fieldProps}
              />
              <ConditionalErrorMessage error={error} />
            </>
          )}
        </Field>
        <Field
          name="resourceName"
          label="Resource Name"
          defaultValue={resourceName}
          isDisabled={isDisabled}
        >
          {({ fieldProps, error }) => (
            <>
              <TextField
                isMonospaced
                spellCheck={false}
                maxLength={MAX_TEXT_LENGTH}
                placeholder="micros-custom-resource (optional)"
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...fieldProps}
              />
              <ConditionalErrorMessage error={error} />
            </>
          )}
        </Field>
        <Field<string, HTMLTextAreaElement>
          name="successExpression"
          label="Success Expression"
          defaultValue={successExpression}
          isDisabled={isDisabled}
        >
          {({ fieldProps, error }) => (
            <>
              {/* @ts-ignore */}
              <TextArea
                isMonospaced
                minimumRows={6}
                spellCheck={false}
                maxLength={MAX_TEXT_LENGTH}
                placeholder="HTTPCode_Target_2XX_Count"
                {...fieldProps}
              />
              <ConditionalErrorMessage error={error} />
            </>
          )}
        </Field>
        <Field<string, HTMLTextAreaElement>
          name="failureExpression"
          label="Failure Expression"
          defaultValue={failureExpression}
          isDisabled={isDisabled}
        >
          {({ fieldProps, error }) => (
            <>
              {/* @ts-ignore */}
              <TextArea
                isMonospaced
                minimumRows={6}
                spellCheck={false}
                maxLength={MAX_TEXT_LENGTH}
                placeholder="HTTPCode_Target_5XX_Count + HTTPCode_ELB_5XX_Count"
                {...fieldProps}
              />
              <ConditionalErrorMessage error={error} />
            </>
          )}
        </Field>
      </>
    )}

    <Field
      name="objective"
      label={
        <FormFieldTooltipLabel isRequired label="SLO - Target" content={helperCopy.slo.sloTarget} />
      }
      validate={validateSLOTarget}
      isRequired
      isDisabled={isDisabled}
      defaultValue={objective}
    >
      {({ fieldProps, error }) => (
        <>
          <TextField
            maxLength={MAX_TEXT_LENGTH}
            placeholder="99.9"
            max={99.9999}
            min={0}
            step={0.001}
            type="number"
            css={sloTargetStyles}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...fieldProps}
          />
          <ConditionalErrorMessage error={error} />
        </>
      )}
    </Field>
  </>
);

export default SliFormFields;
